import React, { useState, useEffect, useRef } from 'react';
import { X, PaperPlaneRight, Lifebuoy } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_QUESTIONS = [
  'What is ConstructHelp?',
  'How do lead credits work?',
  'Is it free to sign up?',
  'How does escrow protect me?',
];

const GREETINGS = [
  "Woof! Need a paw finding anything? 🐾",
  "Hi there! I'm Buddy — ask me anything about ConstructHelp!",
  "Questions about hiring pros or finding work? I can help! 🦴",
];

function getSessionId() {
  let id = localStorage.getItem('buddy_session_id');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `bud-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('buddy_session_id', id);
  }
  return id;
}

export default function BuddyWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoff, setHandoff] = useState({ name: '', email: '', message: '' });
  const [handoffSending, setHandoffSending] = useState(false);
  const [handoffDone, setHandoffDone] = useState(false);
  const [greeting, setGreeting] = useState(null);
  const bottomRef = useRef(null);
  const loadedRef = useRef(false);
  const sessionId = getSessionId();

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      axios.get(`${API}/buddy/history/${sessionId}`)
        .then(r => { if (r.data?.messages?.length) setMessages(r.data.messages); })
        .catch(() => {});
    }
  }, [open, sessionId]);

  // Greeting bubble: pop once per browser session, a few seconds after load
  useEffect(() => {
    if (open || sessionStorage.getItem('buddy_greeted')) return;
    const showTimer = setTimeout(() => {
      setGreeting(GREETINGS[Math.floor(Math.random() * GREETINGS.length)]);
      sessionStorage.setItem('buddy_greeted', '1');
    }, 4000);
    return () => clearTimeout(showTimer);
  }, [open]);

  useEffect(() => {
    if (!greeting) return;
    const hideTimer = setTimeout(() => setGreeting(null), 14000);
    return () => clearTimeout(hideTimer);
  }, [greeting]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming, open]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput('');
    setShowHandoff(false);
    setMessages(prev => [...prev, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/buddy/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok) {
        const detail = res.status === 429
          ? "Woof, that's a lot of questions! Give me a minute to catch my breath, then try again."
          : "Something went wrong on my end. Please try again!";
        setMessages(prev => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: detail };
          return copy;
        });
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(part.slice(6));
            if (ev.delta) {
              setMessages(prev => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  role: 'assistant',
                  content: (copy[copy.length - 1].content || '') + ev.delta,
                };
                return copy;
              });
            }
          } catch (e) { /* partial frame */ }
        }
      }
    } catch (e) {
      setMessages(prev => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: 'Connection hiccup — please try again!' };
        return copy;
      });
    }
    setStreaming(false);
  };

  const submitHandoff = async () => {
    if (!handoff.name.trim() || !handoff.email.trim() || !handoff.message.trim()) return;
    setHandoffSending(true);
    try {
      const transcript = messages.slice(-6).map(m => `${m.role === 'user' ? 'Visitor' : 'Buddy'}: ${m.content}`).join('\n');
      await axios.post(`${API}/contact`, {
        name: handoff.name.trim(),
        email: handoff.email.trim(),
        subject: 'Buddy chat handoff',
        message: `${handoff.message.trim()}\n\n--- Recent Buddy chat ---\n${transcript}`.slice(0, 4000),
      });
      setHandoffDone(true);
    } catch (e) {
      // keep form open so user can retry
    }
    setHandoffSending(false);
  };

  return (
    <>
      {/* Greeting bubble */}
      {!open && greeting && (
        <div
          data-testid="buddy-greeting"
          className="fixed bottom-[10.5rem] md:bottom-24 right-4 md:right-6 z-[60] max-w-[240px] animate-in fade-in slide-in-from-bottom-2 duration-500"
        >
          <div className="relative bg-white dark:bg-neutral-800 border-2 border-black shadow-[4px_4px_0px_#000] p-3 pr-7 text-sm font-bold cursor-pointer"
            onClick={() => { setGreeting(null); setOpen(true); }}
          >
            {greeting}
            <button
              onClick={(e) => { e.stopPropagation(); setGreeting(null); }}
              data-testid="buddy-greeting-dismiss"
              aria-label="Dismiss greeting"
              className="absolute top-1 right-1 text-neutral-400 hover:text-black dark:hover:text-white"
            >
              <X size={14} weight="bold" />
            </button>
            {/* speech-bubble tail */}
            <div className="absolute -bottom-[9px] right-6 w-4 h-4 bg-white dark:bg-neutral-800 border-b-2 border-r-2 border-black rotate-45" />
          </div>
        </div>
      )}

      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="buddy-open-btn"
          aria-label="Chat with Buddy"
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[60] w-16 h-16 rounded-full border-4 border-black bg-amber-400 shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] hover:-translate-y-0.5 transition-all overflow-hidden"
        >
          <img src="/buddy-small.png" alt="Buddy" className="w-full h-full object-cover scale-125 translate-y-1" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="buddy-panel"
          className="fixed bottom-24 md:bottom-6 right-2 md:right-6 z-[60] w-[calc(100vw-1rem)] max-w-sm bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] flex flex-col"
          style={{ height: 'min(560px, calc(100vh - 140px))' }}
        >
          {/* Header */}
          <div className="bg-amber-400 border-b-4 border-black p-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full border-2 border-black bg-white overflow-hidden flex-shrink-0">
              <img src="/buddy-small.png" alt="Buddy" className="w-full h-full object-cover scale-125 translate-y-0.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black uppercase leading-tight text-black">Buddy</p>
              <p className="text-xs font-bold text-black/70">ConstructHelp Helper</p>
            </div>
            <button
              onClick={() => { setShowHandoff(!showHandoff); setHandoffDone(false); }}
              data-testid="buddy-handoff-btn"
              className="text-xs font-bold bg-white border-2 border-black px-2 py-1 hover:bg-neutral-100 text-black flex items-center gap-1"
            >
              <Lifebuoy size={14} weight="bold" /> Talk to a human
            </button>
            <button onClick={() => setOpen(false)} data-testid="buddy-close-btn" aria-label="Close" className="text-black hover:opacity-70">
              <X size={22} weight="bold" />
            </button>
          </div>

          {/* Handoff form */}
          {showHandoff ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="buddy-handoff-form">
              {handoffDone ? (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-600 p-4 text-sm">
                  <p className="font-bold mb-1">Message sent! 🐾</p>
                  <p>The ConstructHelp team will reply to your email soon.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Leave your question and the ConstructHelp team will get back to you by email.
                  </p>
                  <input
                    value={handoff.name}
                    onChange={(e) => setHandoff(h => ({ ...h, name: e.target.value }))}
                    placeholder="Your name"
                    data-testid="buddy-handoff-name"
                    className="w-full border-2 border-black p-2 text-sm bg-white dark:bg-neutral-800"
                  />
                  <input
                    value={handoff.email}
                    onChange={(e) => setHandoff(h => ({ ...h, email: e.target.value }))}
                    placeholder="Your email"
                    type="email"
                    data-testid="buddy-handoff-email"
                    className="w-full border-2 border-black p-2 text-sm bg-white dark:bg-neutral-800"
                  />
                  <textarea
                    value={handoff.message}
                    onChange={(e) => setHandoff(h => ({ ...h, message: e.target.value }))}
                    placeholder="How can we help?"
                    rows={4}
                    data-testid="buddy-handoff-message"
                    className="w-full border-2 border-black p-2 text-sm bg-white dark:bg-neutral-800"
                  />
                  <button
                    onClick={submitHandoff}
                    disabled={handoffSending}
                    data-testid="buddy-handoff-submit"
                    className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black p-2 text-sm disabled:opacity-60"
                  >
                    {handoffSending ? 'Sending…' : 'Send to the team'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="buddy-messages">
                {messages.length === 0 && (
                  <div className="space-y-3">
                    <div className="bg-neutral-100 dark:bg-neutral-800 border-2 border-black p-3 text-sm">
                      Woof! I'm <b>Buddy</b> 🐶 — ConstructHelp's helper. Ask me anything about finding work,
                      hiring pros, leads, escrow, or how the site works!
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {QUICK_QUESTIONS.map(q => (
                        <button
                          key={q}
                          onClick={() => sendMessage(q)}
                          className="text-xs font-bold border-2 border-black px-2 py-1 bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50"
                          data-testid="buddy-quick-question"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] border-2 border-black p-2.5 text-sm whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-[#2B4C6F] text-white'
                        : 'bg-neutral-100 dark:bg-neutral-800'
                    }`}>
                      {m.content || (streaming && i === messages.length - 1 ? (
                        <span className="inline-flex gap-1">
                          <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" />
                          <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                          <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                        </span>
                      ) : '')}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="border-t-4 border-black p-2 flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
                  placeholder="Ask Buddy anything…"
                  data-testid="buddy-input"
                  className="flex-1 border-2 border-black p-2 text-sm bg-white dark:bg-neutral-800 outline-none"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={streaming || !input.trim()}
                  data-testid="buddy-send-btn"
                  aria-label="Send"
                  className="bg-amber-400 hover:bg-amber-500 border-2 border-black px-3 disabled:opacity-50"
                >
                  <PaperPlaneRight size={18} weight="bold" className="text-black" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
